// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
// Services
import { ApiService, ObjectUtils, SystemApiService } from '@cv-invision/core';
// Models
import { OrderInboxModel, TrackingOption } from '../model/order-inbox.model'
import { ReasonType, ReOpenPayload } from '../model/reason-types';
import { RecallPayloadModel } from '../resubmit-order/model/resubmit-order.model';
import { QueueModel, QueuePayloadModel } from '../model/queues.model';
//Components
import { ResubmitCreatedOrder } from '../resubmit-order/resubmit-order.component';
// Others
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class OrderInboxService {
	private headerInboxSubject = new BehaviorSubject({});
	currentHeaderInbox = this.headerInboxSubject.asObservable();
	constructor(
		private httpClient: HttpClient,
		private systemApiService: SystemApiService,
		private api: ApiService) { }

	/**
	 * Get current inbox Orders
	 */
	public getReviewerInbox(params: any): Observable<OrderInboxModel[]> {

		let httpParams = new HttpParams();

		if (params.orderStates.length) {
			params.orderStates.forEach((state: any) => {
				httpParams = httpParams.append('OrderState', state.name);
			});
		}
		if (params.startDate && params.startDate !== null) {
			httpParams = httpParams.append(
				'StartDate',
				params.startDate ? new Date(params.startDate).toISOString() : ''
			);
		}
		if (params.endDate && params.endDate !== null) {
			httpParams = httpParams.append(
				'EndDate',
				params.endDate ? new Date(params.endDate).toISOString() : '');
		}
		if (params.address) {
			httpParams = httpParams.append('Description', params.address);
		}

		if (params.clientName) {
			httpParams = httpParams.append('Client', params.clientName);
		}

		if (params.referenceID) {
			httpParams = httpParams.append('ExternalId', params.referenceID);
		}
		if (params.ssrFilterOrders && params.ssrFilterOrders.id != null) {
			const hasSsrRules = !!params.ssrFilterOrders.id;
			httpParams = httpParams.append('hasSsrRules', hasSsrRules.toString());
		}
		if (params.formType.length) {
			params.formType.forEach((form: any) => {
				httpParams = httpParams.append('FormType', form.name);
			});
		}

		if (params.trackingClients?.trackingId?.length) {
			httpParams = httpParams.append('TrackingSystem', params.trackingClients.trackingId);
		}

		if (params.trackingClients?.clients?.length) {
			params.trackingClients.clients.forEach((client: any) => {
				httpParams = httpParams.append('ClientId', client.id);
			});
		}

		if (params.smartQC.length) {
			params.smartQC.forEach((form: any) => {
				httpParams = httpParams.append('SmartQCStatus', form.id);
			});
		}

		if (params.queues !== undefined && params.queues.length) {
			params.queues.forEach((queue: any) => {
				httpParams = httpParams.append('QueueId', queue.id);
			});
		}

		httpParams = httpParams.append('limit', params.pagination.limit.toString());
		httpParams = httpParams.append('offset', params.pagination.offset.toString());
		if (params.profileSelected && params.profileSelected.id) {
			httpParams = httpParams.append('OrderedBy', params.profileSelected.id.toString());
		}

		return this.httpClient.get(
			this.systemApiService.api['intelligenceService'] + 'order-details/search', { params: httpParams })
			.pipe(
				map((payload: any) => {
					return payload;
				})
			);
	}

	/**
	 * Get the main picture to show in the Inbox Detail
	 */
	public getInboxMainPicture(orderId: number): Observable<OrderInboxModel> {
		return this.httpClient.get(this.systemApiService.api.intelligenceService +
			`orders/${orderId}/documents/current/attachments?category=PhotoRequest`)
			.pipe(
				map((payload: any) => {
					let mainPicture;
					if (payload.data && payload.data.length) {
						mainPicture = payload.data.find((attachment: any) => {
							return (
								attachment.metadata &&
								(attachment.metadata.photoType === 'VIEW_FRONT' ||
									attachment.metadata.photoType === 'STRUCTURE_FRONT')
							);
						});

						// If not mainPicture let's get the first image from the attachments
						if (!mainPicture) {
							mainPicture = payload.data.find((attachment: any) => {
								return attachment.contentType && (attachment.contentType === 'image/jpeg' || attachment.contentType === 'image/jpg') &&
									attachment.groupName === 'Subject';
							});
						}
					}
					return mainPicture;
				})
			);
	}

	/**
	 * Get all profiles
	 */
	public getProfiles(): Observable<any[]> {
		return this.httpClient.get(this.systemApiService.api['intelligenceService'] + `profiles`)
			.pipe(
				map((payload: any) => {
					const profiles = [];
					if (payload.data && payload.data.length) {
						payload.data.forEach((profile: any) => {
							profiles.push({
								id: profile.id,
								name: profile.firstName + ' ' + profile.lastName
							});
						});
					}
					return profiles;
				})
			);
	}

	/**
	 * Re-open an order
	 * @param orderId
	 * @param {ReOpenPayload} reOpenPayload - The reason type and reason text.
	 */
	public reOpenOrder(orderId: number, reOpenPayload: ReOpenPayload): Observable<any> {
		const params = new HttpParams()
			.set('ReOpenReasonType', reOpenPayload.ReOpenReasonType)
			.set('Reason', reOpenPayload.Reason);
		return this.httpClient.put(
			this.systemApiService.api['intelligenceService'] + `orders/${orderId}/reopen`, {}, { params }
		).pipe(map(payload => payload));
	}

	/**
	 * re-submit an order that was imported
	 */
	public reSubmitOrderImported(recallPayload: RecallPayloadModel): Observable<any> {
		const payload = ObjectUtils.recursiveDeepCopy(recallPayload);
		return this.httpClient.post(
			this.systemApiService.api['intelligenceService'] + `orders/review/recall`, { ...payload }
		).pipe(
			map(payload => payload)
		);
	}

	/**
	 * re-submit an order that was creaded via xml
	 * @param payloadForm
	 */
	public reSubmitOrderCreated(payloadForm: ResubmitCreatedOrder): Observable<any> {
		const formFile = new FormData();
		formFile.append('file', payloadForm.file);

		return this.httpClient.put(
			this.systemApiService.api['intelligenceService'] + `upload/orders/${payloadForm.orderId}/resubmit`, formFile)
			.pipe(
				map(payload => payload)
			);
	}

	public resubmitManuallyCreatedOrder(formData: FormData, orderId): Observable<any> {
		return this.httpClient.put(
			this.systemApiService.api['intelligenceService'] + `orders/${orderId}/resubmit`,
			formData
		).pipe(
			map((payload: any) => payload)
		);
	}

	/**
	 * Sets new value for the Header <-> Inbox component data communication.
	 * @param data
	 */
	setHeaderInboxData(data: any): void {
		this.headerInboxSubject.next(data);
	}

	/**
	 *
	 * @param orderId string
	 */
	public getOrderAutoQcStatus(orderId: string): Observable<any> {
		return this.httpClient.get(`${this.systemApiService.api['intelligenceService']}smartQc/process/${orderId}`);
	}

	/**
	  * Gets a trackings collection from the api.
	  *
	  * @returns {Observable<TrackingOption[]>} - An observable of a trackings collection.
	  */
	public getTrackings(): Observable<TrackingOption[]> {
		return this.httpClient
			.get<{ id: string; title: string }[]>(
				`${this.systemApiService.api['orders']}/utils/tracking-systems`
			)
			.pipe(
				map(response =>
					response.map(tracking => ({
						id: tracking.id,
						title: tracking.title
					} as TrackingOption))
				)
			);
	}

	/**
	 * GET - Returns the list of ruleSet clients by tracking system.
	 */
	public getClients(trackingId: string, search?: string): Observable<any> {
		let httpParams: any = new HttpParams();
		httpParams = httpParams.append('limit', 50);
		if (search) {
			httpParams = httpParams.append('title', search.toString());
		}

		return this.httpClient.get(
			`${this.systemApiService.api['orders']}/utils/${trackingId.toLowerCase()}/lenders`,
			{
				params: httpParams,
			},
		);
	}

	/**
	 * Gets the organizations from the users api
	 * @param {string} search - Filters the organizations by name.
	 */
	public getOrganizations(search?: string) {
		const provider = 'classvaluation';

		let params: HttpParams = new HttpParams();
		if (search) {
			params = params.set('Name', search);
		}

		return this.httpClient.get(`${this.systemApiService.api['identitySystemService']}${provider}/organizations`, { params }).pipe(
			map((response: any) => {
				let organizations: TrackingOption[] = [];
				if (response.data) {
					organizations = response.data.map((organization) => {
						const organizationObj: TrackingOption = {
							id: organization.id ?? '',
							title: organization.name ?? '',
						}
						return organizationObj;
					});
				}
				return organizations;
			})
		)
	}

	/**
	 * Gets the reason types from the orders api.
	 * Formats the corresponding reason types.
	 * @returns {ReasonType[]}
	 */
	public getReasonTypes(): Observable<ReasonType[]> {
		return this.httpClient.get<ReasonType[]>(`${this.systemApiService.api['intelligenceService']}orders/reason-types`).pipe(map(response => {
			let reasonTypes: ReasonType[] = [];
			return reasonTypes = response.map((rt => {
				return {
					label: rt.label ?? '',
					key: rt.key ?? ''
				}
			}))
		}));
	}

	/**
	 * Overwrites the CU Score from a order by documentId
	 * @param orderId
	 * @param documentId
	 * @param cuscore
	 * @returns {Observable<any>}
	 */
	public overwriteCUScore(orderId: string, documentId: string, cuscore: number): Observable<any> {
		const payload = { cuscore };
		return this.httpClient.post(
			`${this.systemApiService.api['intelligenceService']}orders/${orderId}/documents/${documentId}/overwrite-ucdp-event`,
			payload
		);
	}

	/**
	 * Overwrites the CU Score from a order by documentId
	 * @returns {Observable<any>}
	 */
	public overwriteCUScoreV2(payload: {orderId: string, documentId: string, form: any, files: any}): Observable<any> {

		const formData = new FormData();

		Object.keys(payload.form).forEach(key => {
			if (payload.form[key]) {
				formData.append(key, payload.form[key]);
			}
		});

		Object.keys(payload.files).forEach(key => {
			if (payload.files[key].blob !== null) {
				formData.append(key, payload.files[key].blob);
			}
		});

		return this.httpClient.post(
			`${this.systemApiService.api['intelligenceService']}orders/${payload.orderId}/documents/${payload.documentId}/overwrite-ucdp-data`,
			formData
		);
	}

	/**
	 * Get list of Queues
	 * @returns {Observable:Array<QueueModel>}
	 */

	public getQueues(): Observable<Array<QueueModel>> {
		return this.httpClient.get<Array<QueuePayloadModel>>(`${this.systemApiService.api['intelligenceService']}orders/queues`).pipe(
			map((payload) => {
				const queues: Array<QueueModel> = [];
				if (payload && payload.length) {
					payload.forEach((queue: QueuePayloadModel) => {
						queues.push({
							id: queue.queueId,
							name: queue.queueName
						});
					});
				}
				return queues;
			})
		);
	}

	/**
	 * Resolves the queues of a selected order Id
	 * @param orderId
	 */
	public resolveQueuesByOrderId(orderId: number): Observable<any> {
		return this.httpClient.post(
            `${this.systemApiService.api['intelligenceService']}manager/queues/resolves/${orderId}`, {}
        );
	}
}
